import React from "react";

/**
 * Small priority pill for announcement posts. Rendered next to a
 * post / row title (and near the article title on the single page)
 * when the post carries `announcementContext.priority`.
 *
 * Priorities: 'low' | 'normal' | 'high' | 'urgent'. `normal` (and an
 * absent priority) render nothing by default — most announcements are
 * "normal" and a badge on every one would be noise. `high` and
 * `urgent` are visually prominent (amber / rose). `low` is a muted
 * grey pill. Pass `showNormal` to force a (subtle blue) badge for the
 * normal level.
 */
export type AnnouncementPriority = "low" | "normal" | "high" | "urgent";

type LangKey = "en" | "mm";

interface PriorityBadgeProps {
  priority?: string | null;
  language?: LangKey;
  /** Render a (subtle) badge for the `normal` level too. Default: hide. */
  showNormal?: boolean;
  className?: string;
}

const LABELS: Record<AnnouncementPriority, Record<LangKey, string>> = {
  low: { en: "Low", mm: "နိမ့်" },
  normal: { en: "Normal", mm: "ပုံမှန်" },
  high: { en: "High", mm: "မြင့်" },
  urgent: { en: "Urgent", mm: "အရေးပေါ်" },
};

// Tailwind utility sets per level. Kept as static class strings (not
// interpolated) so Tailwind's JIT keeps them in the build.
const STYLES: Record<AnnouncementPriority, string> = {
  low: "border-slate-300/60 bg-slate-100 text-slate-600 dark:border-slate-500/40 dark:bg-slate-500/10 dark:text-slate-300",
  normal:
    "border-blue-300/60 bg-blue-50 text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300",
  high: "border-amber-400/60 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300",
  urgent:
    "border-rose-400/60 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-300",
};

const DOT: Record<AnnouncementPriority, string> = {
  low: "bg-slate-400",
  normal: "bg-blue-500",
  high: "bg-amber-500",
  urgent: "bg-rose-500",
};

function normalize(priority?: string | null): AnnouncementPriority | null {
  if (!priority) return null;
  const p = String(priority).toLowerCase();
  if (p === "low" || p === "normal" || p === "high" || p === "urgent") {
    return p;
  }
  return null;
}

export function PriorityBadge({
  priority,
  language = "en",
  showNormal = false,
  className = "",
}: PriorityBadgeProps) {
  const level = normalize(priority);
  if (!level) return null;
  if (level === "normal" && !showNormal) return null;

  const text = LABELS[level][language] ?? LABELS[level].en;
  const prominent = level === "high" || level === "urgent";

  return (
    <span
      role="status"
      aria-label={text}
      className={
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide " +
        (prominent ? "font-bold" : "font-semibold") +
        " " +
        STYLES[level] +
        " " +
        className
      }
    >
      <span
        aria-hidden
        className={"inline-block h-1.5 w-1.5 rounded-full " + DOT[level]}
      />
      {text}
    </span>
  );
}

export default PriorityBadge;
